import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Zap, Package, Eye, ArrowRight, Play,
  CheckCircle2, Clock, Bookmark, Sparkles
} from 'lucide-react';
import { ErrorBanner } from '../components/ErrorBanner';
import { getStepMeta, getDashboardSubline } from '../lib/stepIcons';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PATH_INFO = {
  path_fast_cash: { name: 'Fast Cash Path', icon: Zap, color: '#10B981', slug: 'fast-cash' },
  path_digital_product: { name: 'Simple Digital Product Path', icon: Package, color: '#8B5CF6', slug: 'digital-product' },
  path_faceless_affiliate: { name: 'Faceless Affiliate Path', icon: Eye, color: '#F59E0B', slug: 'faceless-affiliate' }
};

const Dashboard = () => {
  const { user, profile, currentPath, session, refreshProfile, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [pathData, setPathData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [progress, setProgress] = useState([]);
  const [savedOutputs, setSavedOutputs] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!currentPath?.path_id) {
      setLoading(false);
      return;
    }

    setError(null);
    try {
      const token = session?.access_token;
      const authHeaders = getAuthHeaders();
      const fetchOpts = token ? { headers: authHeaders } : {};

      const [pathRes, stepsRes, progressRes, outputsRes, subRes] = await Promise.all([
        fetch(`${API}/paths/${currentPath.path_id}`),
        fetch(`${API}/paths/${currentPath.path_id}/steps`),
        fetch(`${API}/progress/me/${currentPath.path_id}`, { headers: authHeaders }),
        fetch(`${API}/outputs/me?saved_only=true`, { headers: authHeaders }),
        token ? fetch(`${API}/subscription/status`, fetchOpts) : Promise.resolve(null)
      ]);

      if (progressRes.status === 401 || outputsRes.status === 401) {
        setError({ message: 'Your session has expired. Please sign in again.', isAuth: true });
        return;
      }

      if (!pathRes.ok || !stepsRes.ok) {
        setError({ message: 'Failed to load your path data. The server may be temporarily unavailable.' });
        return;
      }

      setPathData(await pathRes.json());
      setSteps(await stepsRes.json());
      if (progressRes.ok) setProgress(await progressRes.json());
      if (outputsRes.ok) {
        const outputs = await outputsRes.json();
        setSavedOutputs(outputs.slice(0, 3));
      }
      if (subRes?.ok) setSubscription(await subRes.json());
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Error fetching dashboard data:', err);
      setError({ message: 'Could not connect to the server. Check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  }, [currentPath, session, getAuthHeaders]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const completedSteps = progress.filter(p => p.is_completed).length;
  const progressPercentage = steps.length > 0 ? (completedSteps / steps.length) * 100 : 0;
  const currentStepNumber = currentPath?.current_step_number || 1;
  const currentStep = steps.find(s => s.step_number === currentStepNumber);
  const pathInfo = PATH_INFO[currentPath?.path_id] || {};
  const PathIcon = pathInfo.icon || Zap;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <ErrorBanner
          message={error.message}
          isAuthError={error.isAuth}
          onRetry={!error.isAuth ? () => { setLoading(true); fetchData(); } : undefined}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Welcome */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 sm:mb-8"
      >
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-1" data-testid="dashboard-welcome">
          Welcome back, {profile?.display_name || user?.email?.split('@')[0]}
        </h2>
        <p className="text-muted-foreground text-sm sm:text-base" data-testid="dashboard-subline">
          {getDashboardSubline(progressPercentage)}
        </p>
      </motion.div>

      {/* Subscription Usage Badge */}
      {subscription && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.03 }}
          className="mb-4"
        >
          <div className="flex items-center justify-between bg-white/70 backdrop-blur-sm rounded-xl px-4 py-3 border border-border/50" data-testid="usage-badge">
            <div className="flex items-center gap-2.5">
              <div className="inline-flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-3 py-1">
                <Sparkles className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold">{subscription.plan_name} Plan</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {subscription.monthly_generations === -1
                  ? 'Unlimited generations'
                  : `${subscription.generations_used}/${subscription.monthly_generations} used this month`}
              </span>
            </div>
            {subscription.plan_id === 'free' && (
              <Link to="/pricing">
                <Button size="sm" className="bg-creataby-gradient-btn text-white hover:opacity-90 text-xs h-7" data-testid="upgrade-btn">
                  Upgrade
                </Button>
              </Link>
            )}
          </div>
        </motion.div>
      )}

      {/* Current Path + Next Step — Primary CTA */}
      {pathData && currentStep && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <Card className="border-border border-l-4 border-l-teal-500 overflow-hidden creataby-card-hover creataby-lightning-watermark" data-testid="path-card">
            <div className="h-1.5" style={{ backgroundColor: pathInfo.color }} />
            <CardContent className="pt-5 pb-5">
              {/* Path info row */}
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${pathInfo.color}1f` }}
                >
                  <PathIcon className="w-6 h-6" style={{ color: pathInfo.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground text-sm sm:text-base truncate">{pathData.name}</p>
                  <p className="text-xs text-muted-foreground">Step {currentStepNumber} of {steps.length}</p>
                  {pathData.description && (
                    <p className="text-xs text-muted-foreground/80 mt-0.5 line-clamp-1">{pathData.description}</p>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-muted-foreground">{completedSteps} completed</span>
                  <span className="font-medium text-foreground">{Math.round(progressPercentage)}%</span>
                </div>
                <Progress value={progressPercentage} />
              </div>

              {/* Next step CTA */}
              <div className="bg-creataby-gradient rounded-xl p-5 shadow-[0_4px_20px_rgba(14,129,117,0.18)]">
                <p className="text-[11px] uppercase tracking-wider text-white/80 mb-1.5 font-semibold">
                  Your next step
                </p>
                <h3 className="font-semibold text-white mb-1 text-sm sm:text-base">{currentStep.title}</h3>
                <p className="text-sm text-white/85 mb-4 line-clamp-2">{currentStep.description}</p>
                <Link to={`/path/${currentPath.path_id}/step/${currentStep.step_number}`}>
                  <Button 
                    className="bg-white text-teal-700 hover:bg-teal-50 font-semibold w-full sm:w-auto shadow-sm"
                    data-testid="continue-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Continue
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Two-column: Stats + Steps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border border-t-4 border-t-emerald-500 h-full creataby-card-hover" data-testid="stats-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Your Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm">Steps Completed</span>
                  </div>
                  <span className="font-bold">{completedSteps}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="text-sm">Steps Remaining</span>
                  </div>
                  <span className="font-bold">{steps.length - completedSteps}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bookmark className="h-4 w-4 text-violet-500" />
                    <span className="text-sm">Saved Outputs</span>
                  </div>
                  <span className="font-bold">{savedOutputs.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Steps overview */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="border-border border-t-4 border-t-teal-500 h-full creataby-card-hover" data-testid="steps-overview-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">All Steps</CardTitle>
              <Link to={`/path/${currentPath?.path_id}`}>
                <Button variant="ghost" size="sm" className="text-xs">
                  View All <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {steps.slice(0, 5).map((step) => {
                  const isCompleted = progress.find(p => p.step_id === step.id)?.is_completed;
                  const isCurrent = step.step_number === currentStepNumber;
                  const meta = getStepMeta(step.step_number);
                  const MiniIcon = meta.icon;
                  
                  return (
                    <Link 
                      key={step.id} 
                      to={`/path/${currentPath?.path_id}/step/${step.step_number}`}
                      className="block"
                    >
                      <div className={`flex items-center gap-3 px-2.5 py-2 rounded-lg transition-colors ${
                        isCurrent ? 'bg-teal-50' : 'hover:bg-teal-50'
                      }`}>
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                          isCompleted
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white border border-slate-200'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          ) : (
                            <MiniIcon className="h-4 w-4" style={{ color: meta.accent }} strokeWidth={2.2} />
                          )}
                        </div>
                        <span className={`text-sm truncate ${isCurrent ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                          {step.title}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Saved Outputs */}
      {savedOutputs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-4 sm:mt-6"
        >
          <Card className="border-border" data-testid="saved-outputs-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Recent Saved Work</CardTitle>
              <Link to="/workspace">
                <Button variant="ghost" size="sm" className="text-xs">
                  View All <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {savedOutputs.map((output) => (
                  <div key={output.id} className="p-3 bg-secondary/70 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1 font-medium">
                      {output.prompt_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                    <p className="text-sm text-foreground line-clamp-2">
                      {output.generated_content.substring(0, 150)}...
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AppLayout>
  );
};

export default Dashboard;
